import React, { useState, useRef, useEffect } from "react";
import { Box, Button, Container, Divider, Typography, TextField, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ButtonComponent from "../../../Components/ButtonComponent";
import { AuthContainer, AuthWrapper } from "../styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";

import { loginSchema } from "../schemas";
import { useLazyLoginWithGoogleQuery, useLoginMutation } from "../../../store/services/auth/auth.service";
import { IError } from "../../../utils/getErrorMessage";
import { notifyError } from "../../../utils/NotifyError";
import ReCAPTCHA from "react-google-recaptcha";
import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import RecaptchaDisclaimer from "../../../Components/RecaptchaDisclaimer";
import { ILoginIn } from "../../../store/services/auth/dto/auth.dto";
const Login = () => {
	const [showPassword, setShowPassword] = useState(false);
	const [isLoggingIn, setIsLoggingIn] = useState(false);
	const navigate = useNavigate();
	const [callLogin, { isLoading }] = useLoginMutation();
	const [callLoginWithGoogle] = useLazyLoginWithGoogleQuery();
	const [searchParams] = useSearchParams();
	const redirectUrl = searchParams.get("redirect");
	const recaptchaRef = useRef<ReCAPTCHA>(null);

	useEffect(() => {
		return () => {
			if (recaptchaRef.current) recaptchaRef.current.reset();
		};
	}, []);
	const handleOnSubmit = async (values: ILoginIn) => {
		if (!recaptchaRef.current) {
			return notifyError({ error: "Recaptcha failed" } as IError);
		}
		setIsLoggingIn(true);
		let recaptchaToken: string | null = "";
		try {
			recaptchaToken = await recaptchaRef.current.executeAsync();
		} catch (e) {
			console.error(e);
		}

		callLogin({ ...values, email: values.email.toLowerCase(), recaptchaToken }).then(({ data, error }) => {
			if (data) {
				navigate(redirectUrl ? decodeURIComponent(redirectUrl) : "/");
			}
			if (error) {
				notifyError(error as IError);
			}
			setIsLoggingIn(false);
		});
	};

	const {
		handleSubmit,
		handleChange,
		errors,
		values: { email, password },
		dirty,
		isValid
	} = useFormik({
		validationSchema: loginSchema,
		initialValues: {
			email: "",
			password: ""
		},
		onSubmit: handleOnSubmit
	});

	const responseMessage = (response: CredentialResponse) => {
		callLoginWithGoogle({ token: response.credential as string }).then(({ data, error }) => {
			if (data) {
				navigate(redirectUrl ? decodeURIComponent(redirectUrl) : "/");
			}
			if (error) {
				notifyError(error as IError);
			}
		});
	};

	const handleError = () => {
		console.log("error");
	};
	const handleClickShowPassword = () => setShowPassword(show => !show);

	return (
		<Container>
			<AuthContainer>
				<form onSubmit={handleSubmit}>
					<AuthWrapper>
						<Box className={"left_block"}>
							<Typography variant={"h6"} className={"title"}>
								Sign In
							</Typography>
							<Box mt={2} width={"100%"}>
								<Box mb={2} className={"input_wrapper"}>
									<Typography variant={"subtitle1"} fontWeight={"bold"}>
										Email
									</Typography>
									<TextField
										fullWidth
										type={"email"}
										value={email}
										name={"email"}
										onChange={handleChange}
										error={!!errors.email}
										helperText={errors.email}
										autoComplete={"email"}
									/>
								</Box>
								<Box mb={2} className={"input_wrapper"}>
									<Typography variant={"subtitle1"} fontWeight={"bold"}>
										Password
									</Typography>
									<TextField
										fullWidth
										type={showPassword ? "text" : "password"}
										value={password}
										name={"password"}
										onChange={handleChange}
										error={!!errors.password}
										helperText={errors.password}
										autoComplete={"password"}
										slotProps={{
											input: {
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															aria-label={showPassword ? "hide password" : "display password"}
															onClick={handleClickShowPassword}
															edge="end"
														>
															{showPassword ? <VisibilityOff /> : <Visibility />}
														</IconButton>
													</InputAdornment>
												)
											}
										}}
									/>
								</Box>
							</Box>
							<Box width={"100%"}>
								<ButtonComponent
									fullWidth
									variant={"contained"}
									type="submit"
									disabled={(dirty && !isValid) || isLoading || isLoggingIn}
									className={"signBtn"}
									text={"Sign In"}
									loading={isLoading || isLoggingIn}
								/>
							</Box>
							<RecaptchaDisclaimer />
							<Box width={"100%"} display={"flex"} justifyContent={"flex-end"}>
								<Button variant={"text"} onClick={() => navigate("/resetPassword")}>
									Forgot Password
								</Button>
							</Box>
							<Box width={"100%"} my={2}>
								<Divider />
							</Box>

							<Box
								display={"flex"}
								justifyContent={"center"}
								width={"100%"}
								py={1}
								flexDirection={"column"}
								alignItems={"center"}
								gap={3}
							>
								<Typography variant={"subtitle1"}>or Login with</Typography>
								<GoogleLogin onSuccess={responseMessage} onError={handleError} />
							</Box>
						</Box>

						<Box className={"right_block"}>
							<Typography variant={"h4"}>Welcome to login</Typography>
							<Typography variant={"subtitle2"}>Don't have an account?</Typography>
							<Button variant={"contained"} size={"large"} onClick={() => navigate("/signup")}>
								Sign Up
							</Button>
						</Box>
					</AuthWrapper>
				</form>
			</AuthContainer>

			<ReCAPTCHA
				sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string}
				size="invisible"
				ref={recaptchaRef}
				badge="bottomright"
			/>
		</Container>
	);
};

export default Login;
